import { Editor, Range, Transforms } from 'slate';

import type {
  SuggestionActionType,
  SuggestionMarkerEndElement,
  SuggestionMarkerStartElement,
} from '../internal';
import {
  createDefaultText,
  isSuggestionMarkerEndElement,
  isSuggestionMarkerStartElement,
  Queries,
  suggestionMarkerEndType,
  suggestionMarkerStartType,
} from '../internal';

export interface InsertSuggestionMarkersOptions {
  at: Range;
  authorId: string;
  reasoning: string;
  suggestionActionType: SuggestionActionType;
  suggestionId: string;
}

const SuggestionMarkerTransforms = {
  /**
   * Insert suggestion markers wrapping a range.
   */
  insertSuggestionMarkers: (editor: Editor, options: InsertSuggestionMarkersOptions): void => {
    const { at, authorId, reasoning, suggestionActionType, suggestionId } = options;
    const markerId = Queries.getNextAvailableSuggestionMarkerId(editor);

    const startElement: SuggestionMarkerStartElement = {
      authorId,
      children: [createDefaultText()],
      markerId,
      reasoning,
      suggestedEdits: [],
      suggestionActionType,
      suggestionId,
      type: suggestionMarkerStartType,
    };

    const endElement: SuggestionMarkerEndElement = {
      children: [createDefaultText()],
      markerId,
      suggestedEdits: [],
      type: suggestionMarkerEndType,
    };

    const [start, end] = Range.edges(at);

    // Insert end marker first so the start path remains valid
    Transforms.insertNodes(editor, endElement, { at: end, voids: true });
    Transforms.insertNodes(editor, startElement, { at: start, voids: true });
  },

  /**
   * Remove suggestion markers by suggestionId.
   */
  removeSuggestionMarkers: (editor: Editor, suggestionId: string): boolean => {
    const { end, start } = Queries.getSuggestionById(editor, suggestionId);
    if (!start) return false;

    // Find and remove the end marker first (if it exists)
    if (end) {
      const endEntry = Editor.nodes(editor, {
        at: [],
        match: node => isSuggestionMarkerEndElement(node) && node.markerId === start.markerId,
      }).next().value;

      if (endEntry) {
        Transforms.removeNodes(editor, { at: endEntry[1], voids: true });
      }
    }

    // Find and remove the start marker
    const startEntry = Editor.nodes(editor, {
      at: [],
      match: node => isSuggestionMarkerStartElement(node) && node.suggestionId === suggestionId,
    }).next().value;

    if (startEntry) {
      Transforms.removeNodes(editor, { at: startEntry[1], voids: true });
    }

    return true;
  },

  /**
   * Update the suggestionActionType on a suggestion.
   */
  updateSuggestionActionType: (
    editor: Editor,
    options: { suggestionActionType: SuggestionActionType; suggestionId: string }
  ): boolean => {
    const { suggestionActionType, suggestionId } = options;

    const startEntry = Editor.nodes(editor, {
      at: [],
      match: node => isSuggestionMarkerStartElement(node) && node.suggestionId === suggestionId,
    }).next().value;

    if (!startEntry) return false;

    Transforms.setNodes(editor, { suggestionActionType }, { at: startEntry[1], voids: true });

    return true;
  },

  /**
   * Update the reasoning on a suggestion.
   */
  updateSuggestionReasoning: (
    editor: Editor,
    options: { reasoning: string; suggestionId: string }
  ): boolean => {
    const { reasoning, suggestionId } = options;

    const startEntry = Editor.nodes(editor, {
      at: [],
      match: node => isSuggestionMarkerStartElement(node) && node.suggestionId === suggestionId,
    }).next().value;

    if (!startEntry) return false;

    Transforms.setNodes(editor, { reasoning }, { at: startEntry[1], voids: true });

    return true;
  },
};

export default SuggestionMarkerTransforms;
